import React from 'react';
import { Anchor, Box, Card, DetailedIconHome, Grid, Heading, Paragraph } from '@zillow/constellation';
import { StaticMap } from '../StaticMap';
import type { PropertyDetailsResponse, ShareApplicationPropertyAddress } from '../../models';
import { getPropertyType } from '../../utils/shareApplication.utils';

export function ConfirmPropertyAddress({
  propertyAddress,
  propertyDetails,
}: {
  propertyAddress: ShareApplicationPropertyAddress;
  propertyDetails: PropertyDetailsResponse;
}) {
  const { street, city, state, zip, unit } = propertyAddress;
  const displayAddress = `${street}, ${city}, ${state} ${zip}`;
  const propertyType = getPropertyType(propertyDetails.propertyTypeCode);
  return (
    <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" style={{ minHeight: '100%' }}>
      <Grid gridColumnEnd={{ default: 'span 12', lg: 'span 8', xl_gte: 'span 7' }}>
        <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" marginTop="xl">
          <Grid gridColumnEnd={{ default: 'span 1', lg: 'span 2' }}></Grid>
          <Grid gridColumnEnd={{ default: 'span 10', lg: 'span 8' }}>
            <DetailedIconHome marginY="md" />
            <Heading level={4} as="h2" fontFamily="serif">
              Confirm your property’s address
            </Heading>
            <Paragraph marginTop="md">
              The applicant entered this address when sharing their application. Double check for any errors or missing
              information.
            </Paragraph>
            <Card cardElevation={0} cardType="secondary" marginY="md">
              <Heading level={6} as="h3">
                Street address
              </Heading>
              <Paragraph marginBottom="sm">{displayAddress}</Paragraph>
              {unit && (
                <>
                  <Heading level={6} as="h3">
                    Unit number
                  </Heading>
                  <Paragraph marginBottom="sm">{unit}</Paragraph>
                </>
              )}
              {propertyType && (
                <>
                  <Heading level={6} as="h3">
                    Property type
                  </Heading>
                  <Paragraph marginBottom="sm">{propertyType}</Paragraph>
                </>
              )}
              <Paragraph fontColor="textMedium" fontType="finePrint">
                Confirm this is the USPS-validated address. You won’t be able to make edits once you confirm.
              </Paragraph>
            </Card>
            <Paragraph fontColor="textMedium" fontType="legal">
              By continuing, you agree to comply with our{' '}
              <Anchor target="_blank" href="https://www.zillow.com/z/corp/terms/">
                Terms of Use
              </Anchor>
              ,{' '}
              <Anchor as="a" target="_blank" href="https://www.zillow.com/renter-hub/terms/Rental-User-Terms">
                Rental User Terms
              </Anchor>
              , and{' '}
              <Anchor as="a" target="_blank" href="https://www.zillow.com/rental-manager/respectful-renting-pledge/">
                Respectful Renting Pledge
              </Anchor>
              .
            </Paragraph>
          </Grid>
          <Grid gridColumnEnd={{ default: 'span 1', lg: 'span 2' }}></Grid>
        </Grid>
      </Grid>
      <Grid gridColumnEnd={{ default: 'span 12', lg: 'span 4', xl_gte: 'span 5' }}>
        <Box
          sx={{
            backgroundColor: '$colors.backgroundBlue',
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <StaticMap address={displayAddress} size="434x622" />
        </Box>
      </Grid>
    </Grid>
  );
}
